'use client';

import { Button, type IconNames, SkeletonBar } from '@petsmart-ui/sparky';
import cn from 'classnames';

import { AccountDrawerType, useAuthSignInClick, useFavoritesQuery, usePageLayout } from '@libs/common-next';

import { useProduct } from '@/entities/product';
import { usePurchaseFormValue } from '@/entities/purchase-form';
import { setPendingFavorite } from '@/features/favorite-toggle/lib/pending-favorite';

import { useAnalytics, useFavoriteToggleWithAutoAdd, useOnError } from './model';

import './FavoriteToggle.scss';

export const FavoriteToggle = () => {
    const { isAuth0Enabled, handleSignInClick } = useAuthSignInClick();
    const { user, openAccountDrawer, setSignInDrawerProps } = usePageLayout();

    const { product } = useProduct();
    const getPurchaseFormValues = usePurchaseFormValue();

    const { data: favoritesData, isPending: isFavoritesLoading } = useFavoritesQuery();

    const isFavorite = favoritesData?.some(favoriteItem => favoriteItem.sku === product?.sku);

    const {
        mutate: toggleFavoriteMutation,
        isPending: isTogglePending,
        triggerAddAfterLogin,
    } = useFavoriteToggleWithAutoAdd({
        onAddActionCallback: useAnalytics().send,
        onErrorCallback: useOnError(),
        sku: product?.sku,
        userId: user?.customerprofileid?.toString(),
        isFavorite,
        isFavoritesLoading,
    });

    const className = cn('sparky-c-icon-toggle', 'c-heart-toggle', { 'sparky-c-icon-toggle--active': isFavorite });
    const icon: IconNames = isFavorite ? 'heart-fill' : 'heart-outline';

    const handleClick = () => {
        if (!product?.sku || isTogglePending) return;

        if (!user) {
            if (isAuth0Enabled) {
                const { quantity, purchaseOption, autoshipFrequency, autoshipUnit, fulfilmentMethod, personalization } =
                    getPurchaseFormValues();

                setPendingFavorite({
                    webStyleId: product.webStyleId,
                    favoriteSku: product.sku,
                    selections: {
                        quantity: quantity ?? 1,
                        purchaseOption: purchaseOption ?? 'one-time',
                        autoshipFrequency: autoshipFrequency ?? '4',
                        autoshipUnit: autoshipUnit ?? '2',
                        fulfilmentMethod: fulfilmentMethod ?? 'bopis',
                        personalization,
                    },
                });
                handleSignInClick();
                return;
            }

            triggerAddAfterLogin();
            setSignInDrawerProps({ productToAddToWishlist: product.sku });
            openAccountDrawer(AccountDrawerType.SIGN_IN);
            return;
        }

        toggleFavoriteMutation({
            sku: product.sku,
            action: isFavorite ? 'remove' : 'add',
        });
    };

    if (user && isFavoritesLoading) {
        return <SkeletonBar className="favorite-toggle__skeleton" />;
    }

    return (
        <Button
            hideText
            iconSize="md"
            variant="bare"
            iconName={icon}
            className={className}
            onClick={handleClick}
            aria-pressed={isFavorite}
            text="Favorite toggle button"
            data-testid="test-favorite-toggle"
        />
    );
};
